import { Box, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { Button, TextField, Stack, IconButton, Checkbox } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { addProductSizes, updateProductSizes, deleteProductSizes } from "../../../../../services/Admin/ProductsMediaAndDetailsApi";

const ProductSizesTab = ({ productId, sizes = [], onSuccess }) => {
  const [newSizes, setNewSizes] = useState([]);
  const [editableSizes, setEditableSizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedSizeIds, setSelectedSizeIds] = useState([]);

  useEffect(() => {
    // initialize editableSizes from incoming sizes prop
    setEditableSizes(Array.isArray(sizes) ? sizes.map((s) => ({ id: s.id, key: s.key || "", size: s.size || "" })) : []);
  }, [sizes]);

  const handleAddRow = () => {
    setNewSizes((s) => [...s, { key: "", size: "" }]);
  };

  const handleRemove = (index) => {
    setNewSizes((s) => s.filter((_, i) => i !== index));
  };

  const handleChange = (index, field, value) => {
    setNewSizes((s) => s.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleExistingChange = (index, field, value) => {
    setEditableSizes((s) => s.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const toggleSelect = (id) => {
    if (!id) return;
    setSelectedSizeIds((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  };

  const handleDeleteSingle = async (id) => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!id) return;
    setLoading(true);
    try {
      await deleteProductSizes([id], { product_id: productId });
      setEditableSizes((s) => s.filter((x) => x.id !== id));
      setSelectedSizeIds((s) => s.filter((x) => x !== id));
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف المقاس");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!selectedSizeIds.length) {
      setError("اختر مقاسًا واحدًا على الأقل للحذف.");
      return;
    }
    setLoading(true);
    try {
      await deleteProductSizes(selectedSizeIds, { product_id: productId });
      setEditableSizes((s) => s.filter((x) => !selectedSizeIds.includes(x.id)));
      setSelectedSizeIds([]);
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل حذف المقاسات");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!editableSizes.length) {
      setError("لا توجد مقاسات لتعديلها.");
      return;
    }
    setLoading(true);
    try {
      // نرسل جميع المقاسات المعدلة (تحتوي على id) إلى endpoint التحديث
      await updateProductSizes(editableSizes, { product_id: productId });
      if (onSuccess) await onSuccess();
    } catch (err) {
      setError(err?.message || "فشل تحديث المقاسات");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!productId) {
      setError("productId مطلوب.");
      return;
    }
    if (!newSizes.length) {
      setError("أضف مقاسًا واحدًا على الأقل.");
      return;
    }
    setLoading(true);
    try {
      // إرسال productId كأول معامل ولإضافة في params أيضاً ليدخل في طلب الخادم إن لزم
      await addProductSizes(productId, newSizes, { product_id: productId });
      setNewSizes([]);
      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err?.message || "فشل إضافة المقاسات");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>المقاسات والأحجام</Typography>

      {/* عرض وتحرير المقاسات الحالية */}
      {editableSizes && editableSizes.length ? (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button
              onClick={handleUpdateSubmit}
              disabled={loading || !editableSizes.length || !productId}
              size="small"
              variant="contained"
            >
              {loading ? "جارٍ..." : "حفظ التعديلات"}
            </Button>
            <Button
              onClick={handleBulkDelete}
              disabled={loading || !selectedSizeIds.length || !productId}
              size="small"
              variant="outlined"
              color="error"
            >
              حذف المحدد ({selectedSizeIds.length})
            </Button>
          </Stack>

          {editableSizes.map((s, i) => (
            <Stack key={s.id ?? i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Checkbox
                checked={selectedSizeIds.includes(s.id)}
                onChange={() => toggleSelect(s.id)}
                disabled={!s.id}
                size="small"
              />
              <TextField
                size="small"
                label="المفتاح"
                value={s.key}
                onChange={(e) => handleExistingChange(i, "key", e.target.value)}
              />
              <TextField
                size="small"
                label="الحجم"
                value={s.size}
                onChange={(e) => handleExistingChange(i, "size", e.target.value)}
              />
              <IconButton
                size="small"
                onClick={() => handleDeleteSingle(s.id)}
                aria-label="delete-existing"
                disabled={!s.id || loading || !productId}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </>
      ) : (
        <Typography color="text.secondary">لا توجد مقاسات</Typography>
      )}

      {/* إضافة مقاسات جديدة */}
      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} size="small" variant="contained">
            إضافة صف
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !newSizes.length || !productId}
            size="small"
            variant="outlined"
          >
            {loading ? "جارٍ..." : "حفظ المقاسات"}
          </Button>
        </Stack>

        {error ? <Typography color="error" sx={{ mb: 1 }}>{error}</Typography> : null}

        {newSizes.map((r, idx) => (
          <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              size="small"
              label="المفتاح"
              value={r.key}
              onChange={(e) => handleChange(idx, "key", e.target.value)}
            />
            <TextField
              size="small"
              label="الحجم"
              value={r.size}
              onChange={(e) => handleChange(idx, "size", e.target.value)}
            />
            <IconButton size="small" onClick={() => handleRemove(idx)} aria-label="remove">
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Box>
    </Box>
  );
};

export default ProductSizesTab;
